import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { fetchProjectById } from '../services/projectService';
import PolygonMap from './PolygonMap';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const data = await fetchProjectById(id);
        setProject(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProject();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Message
          severity="error"
          text={`Error al cargar el proyecto: ${error}`}
          className="w-full"
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex justify-content-center p-4">
        <p>Proyecto no encontrado</p>
      </div>
    );
  }

  const getStatusSeverity = (status) => {
    switch (status?.name) {
      case 'CERTIFICADO':
        return 'success';
      case 'EN_DESARROLLO':
        return 'info';
      case 'REGISTRO_COMPLETO':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const header = (
    <div className="flex align-items-center justify-content-between p-3">
      <div className="flex align-items-center gap-3">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text p-button-rounded"
          onClick={() => navigate('/projects')}
          tooltip="Volver"
        />
        <h2 className="m-0">{project.name}</h2>
      </div>
      <div className="flex gap-2">
        <Button
          label="Editar"
          icon="pi pi-pencil"
          className="p-button-primary"
          onClick={() => navigate(`/projects/${id}/edit`)}
        />
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <Card header={header} className="shadow-2">
        <div className="grid">
          {/* Basic Information */}
          <div className="col-12 md:col-6">
            <Card title="Información Básica" className="h-full">
              <div className="grid formgrid">
                <div className="col-12 mb-3">
                  <strong>Ubicación:</strong> {project.location}
                </div>
                <div className="col-12 mb-3">
                  <strong>Departamento:</strong> {project.departamento}
                </div>
                <div className="col-12 mb-3">
                  <strong>Estado:</strong>
                  <Badge
                    value={project.status?.name}
                    severity={getStatusSeverity(project.status)}
                    className="ml-2"
                  />
                </div>
                <div className="col-12 mb-3">
                  <strong>Hectáreas:</strong> {project.hectares?.toLocaleString()} ha
                </div>
              </div>
            </Card>
          </div>

          {/* Description */}
          <div className="col-12 md:col-6">
            <Card title="Descripción" className="h-full">
              <p className="m-0 text-justify">{project.description}</p>
            </Card>
          </div>

          {/* Dates */}
          <div className="col-12 md:col-6">
            <Card title="Fechas del Proyecto" className="h-full">
              <div className="grid formgrid">
                <div className="col-12 mb-2">
                  <strong>Fecha de Inicio:</strong> {formatDate(project.startDate)}
                </div>
                <div className="col-12 mb-2">
                  <strong>Fecha de Fin:</strong> {formatDate(project.endDate)}
                </div>
                <div className="col-12 mb-2">
                  <strong>PDD Inicio:</strong> {formatDate(project.pddStartDate)}
                </div>
                <div className="col-12 mb-2">
                  <strong>PDD Fin:</strong> {formatDate(project.pddEndDate)}
                </div>
                <div className="col-12 mb-2">
                  <strong>Registro:</strong> {formatDate(project.registrationDate)}
                </div>
                <div className="col-12 mb-2">
                  <strong>Consulta:</strong> {formatDate(project.queryDate)}
                </div>
              </div>
            </Card>
          </div>

          {/* Stakeholders */}
          <div className="col-12 md:col-6">
            <Card title="Actores Involucrados" className="h-full">
              <div className="grid formgrid">
                <div className="col-12 mb-3">
                  <strong>Comunidades:</strong>
                  <p className="m-0 mt-1 text-sm">{project.communitiesInvolved}</p>
                </div>
                <Divider />
                <div className="col-12 mb-3">
                  <strong>Desarrolladores:</strong>
                  <p className="m-0 mt-1 text-sm">{project.developerOrganizations}</p>
                </div>
                <Divider />
                <div className="col-12 mb-3">
                  <strong>Colaboradores:</strong>
                  <p className="m-0 mt-1 text-sm">{project.collaboratingOrganizations}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Technical Information */}
          <div className="col-12">
            <Card title="Información Técnica">
              <div className="grid">
                <div className="col-12 md:col-4 mb-3">
                  <strong>Validador:</strong> {project.validatorString || 'N/A'}
                </div>
                <div className="col-12 md:col-4 mb-3">
                  <strong>Certificador:</strong> {project.certifier?.name || 'N/A'}
                </div>
                <div className="col-12 md:col-4 mb-3">
                  <strong>Verificadores:</strong> {project.verifiersString || 'N/A'}
                </div>
                {project.link && (
                  <div className="col-12 mb-3">
                    <strong>Enlace:</strong>
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="ml-2">
                      {project.link}
                    </a>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Map Visualization */}
          {project.locationGeometryWkt && (
            <div className="col-12">
              <Card title="Visualización del Polígono">
                <PolygonMap wkt={project.locationGeometryWkt} />
              </Card>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProjectDetails;